import React, { useCallback, useMemo } from 'react';
import { useRelayEnvironment } from 'react-relay';
import type { TerminalClause } from '@atlaskit/jql-ast';
import type { ActionMeta } from '@atlaskit/select';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type {
	SelectedValue,
	SelectOption,
	OnChangeOperator,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { createGenericDebouncableDataLoader } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/debouncer.tsx';
import { isSingleValueOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/is-single-value-option.tsx';
import removeFieldClauseFromJql from '@atlassian/jira-jql-utils/src/utils/remove-field-clause.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { AdvancedContent$data } from '@atlassian/jira-filter-refinement/src/ui/filter-popup/filter-popup-content/ui/advanced/__generated__/AdvancedContent.graphql';
import type {
	useHydrationQueryLoaderQuery$data,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { JQLBuilderUI$data } from '@atlassian/jira-jql-builder/src/ui/__generated__/JQLBuilderUI.graphql';
import {
	type PickerData,
	type OnSearchWrapper,
	GenericPropTypes,
} from '../../../../common/types.tsx';
import type { JiraProjectOptionsType } from '../../../../common/ui/types.tsx';
import { useAST } from '../../../../controllers/ast/index.tsx';
import { changeOperator } from '../../../../utils/operators/change-operator.tsx';
import type { AnalyticsProps } from '../../../../utils/send-option-analytics.tsx';
import { updateSelectedValue } from '../../../../utils/update-selected-value/index.tsx';
import messages from './messages.tsx';
import { PickerLoader } from './picker-loader/index.tsx';
import { getPickerProps } from './props-resolver/index.tsx';
import { useSelectedOperator } from './use-selected-operator/index.tsx';
import { useSelectedValues } from './use-selected-values/index.tsx';

export type PickerWrapperProps = {
	fieldId: string;
	fieldLabel?: string;
	terminalClause: TerminalClause | undefined;
	activePopupKey: string;
	hydratedData: useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data;
	loading: boolean;
	onOpen: () => void;
	onClose: () => void;
	onSearchWrapper?: OnSearchWrapper;
	onSendOptionAnalytics?: (arg1: AnalyticsProps) => void;
	onClear?: (onClearJqlClause: string | undefined) => void;
	picker: PickerData;
	projectOptions?: JiraProjectOptionsType;
	isMulti?: boolean;
	fieldDescription: string | null;
	onOpenOperatorPicker: () => void;
	hideOperatorDropdown?: boolean;
	isIrremovable?: boolean;
	contextJql: string;
	viewContext: JiraJqlViewContext | null;
	scope: JiraJqlScopeInput | null;
	suggestedUsersCount?: number;
};

export const PickerWrapper = ({
	fieldId,
	fieldLabel: fieldDisplayName,
	terminalClause,
	activePopupKey,
	onOpen,
	onClose,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onSearchWrapper = () => {},
	onSendOptionAnalytics,
	hydratedData,
	loading: isLoading,
	onClear,
	picker,
	projectOptions,
	isMulti,
	fieldDescription,
	onOpenOperatorPicker,
	hideOperatorDropdown,
	isIrremovable,
	contextJql,
	viewContext,
	scope,
	suggestedUsersCount,
}: PickerWrapperProps): React.JSX.Element => {
	const refinementInvalidProjectOptionMessageGalaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.refinementInvalidProjectOptionMessageGalaxia
		: messages.refinementInvalidProjectOptionMessage;
	const refinementInvalidOptionMessageGalaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.refinementInvalidOptionMessageGalaxia
		: messages.refinementInvalidOptionMessage;
	const isOpen = activePopupKey === fieldId;
	const isEmpty = !terminalClause;
	const intl = useIntl();
	const [ast] = useAST();

	const environment = useRelayEnvironment();
	const { cloudId } = useTenantContext();
	const { searchTemplate } = picker;

	// FIXME: getPickerProps creates "unstable" results so we've wrapped it in a memo;
	// a better solution might be to make it a hook so we can be more granular on each individual callback etc
	const {
		loadOptions: loadOptionsCallback,
		enableFullList,
		operandDisplayValues,
		showOperandDisplayValuesInFullList,
	} = useMemo(
		() =>
			getPickerProps({
				fieldId,
				propType: picker.propType || GenericPropTypes.NoProp,
				searchTemplate,
				jql: contextJql,
				formatMessage: intl.formatMessage,
				environment,
				cloudId,
				projectOptions,
				viewContext,
				scope,
				suggestedUsersCount,
			}),
		[
			cloudId,
			contextJql,
			environment,
			fieldId,
			intl.formatMessage,
			picker.propType,
			projectOptions,
			scope,
			searchTemplate,
			suggestedUsersCount,
			viewContext,
		],
	);

	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const loadOptions = useCallback(createGenericDebouncableDataLoader(loadOptionsCallback), [
		loadOptionsCallback,
	]);

	const [selectedValues, setSelectedValues] = useSelectedValues(
		terminalClause,
		hydratedData,
		fieldId,
		operandDisplayValues,
		searchTemplate,
	);

	const { selectedOperator, setSelectedOperator, allowedOperators, setDefaultOperator } =
		useSelectedOperator({
			fieldDisplayName: fieldDisplayName ?? fieldId,
			terminalClause,
			allowedOperators: picker.allowedOperators,
		});

	const onChangeOperator = useCallback<OnChangeOperator>(
		(operator) => {
			if (
				!hideOperatorDropdown &&
				operator !== null &&
				operator.value !== selectedOperator?.value
			) {
				setSelectedOperator && setSelectedOperator(operator);
				if (terminalClause) {
					onSearchWrapper(changeOperator(ast, terminalClause, operator.value));
				}
			}
		},
		[
			hideOperatorDropdown,
			selectedOperator?.value,
			setSelectedOperator,
			terminalClause,
			onSearchWrapper,
			ast,
		],
	);

	const isInvalid = useMemo(
		() =>
			!isEmpty &&
			selectedValues.length > 0 &&
			selectedValues.some(
				(selectedValue: SelectOption) =>
					selectedValue.optionType === 'error' ||
					(selectedValue.optionType !== 'group' && selectedValue.invalid),
			),
		[selectedValues, isEmpty],
	);

	type SelectDataTyp = {
		selectedIndex: number;
	};

	const onChangeCallback = useCallback(
		(
			optionOrOptions: SelectedValue,
			meta: ActionMeta<SelectOption>,
			selectData: SelectDataTyp | undefined,
		): void => {
			const options = Array.isArray(optionOrOptions) ? optionOrOptions : [optionOrOptions];

			// Optimistically make the UI change, so we don't have to wait for hydration
			setSelectedValues(options);
			onSearchWrapper?.(
				updateSelectedValue({
					ast,
					fieldId,
					options,
					terminalClause,
					operator: !hideOperatorDropdown ? selectedOperator : undefined,
				}),
			);
			if (!Array.isArray(optionOrOptions)) {
				onClose?.();
			}
			if (meta.option && isSingleValueOption(meta.option)) {
				onSendOptionAnalytics?.({
					selectedItemsCount: options.length,
					selectedItemIndex: selectData?.selectedIndex,
					selectedItemType: 'option' in meta ? meta.option?.itemType : undefined,
					isFullList: 'option' in meta ? meta.option?.fullList : undefined,
				});
			}
		},
		[
			setSelectedValues,
			onSearchWrapper,
			ast,
			fieldId,
			terminalClause,
			hideOperatorDropdown,
			selectedOperator,
			onClose,
			onSendOptionAnalytics,
		],
	);

	const onClearValues = useCallback(() => {
		// Optimistically make the UI change so we don't have to wait for hydration
		setSelectedValues([]);
		terminalClause &&
			onSearchWrapper &&
			onSearchWrapper(removeFieldClauseFromJql(ast, terminalClause));
		if (!hideOperatorDropdown) {
			setDefaultOperator();
		}
	}, [
		setSelectedValues,
		terminalClause,
		onSearchWrapper,
		ast,
		hideOperatorDropdown,
		setDefaultOperator,
	]);

	const handleOnClear = useCallback(() => {
		onClear && onClear(terminalClause ? removeFieldClauseFromJql(ast, terminalClause) : undefined);
	}, [ast, onClear, terminalClause]);

	const refinement = fieldDisplayName?.toLowerCase() || fieldId;
	const invalidMessage =
		refinement === 'project'
			? intl.formatMessage(refinementInvalidProjectOptionMessageGalaxia)
			: intl.formatMessage(refinementInvalidOptionMessageGalaxia, {
					refinement,
				});

	return (
		<PickerLoader
			isOpen={isOpen}
			onOpen={onOpen}
			onClose={onClose}
			value={selectedValues}
			isLoading={isLoading}
			fieldId={fieldId}
			searchTemplate={searchTemplate}
			fieldInvalidLabel={isInvalid ? invalidMessage : undefined}
			onChange={onChangeCallback}
			onClearValues={terminalClause ? onClearValues : undefined}
			onLoadOptions={loadOptions}
			enableFullList={enableFullList}
			operandDisplayValues={operandDisplayValues}
			showOperandDisplayValuesInFullList={showOperandDisplayValuesInFullList}
			clause={terminalClause}
			fieldDisplayName={fieldDisplayName}
			isInvalid={isInvalid}
			isEmpty={isEmpty}
			onClearPicker={handleOnClear}
			onChangeOperator={onChangeOperator}
			allowedOperators={allowedOperators}
			selectedOperator={selectedOperator}
			hideOperatorDropdown={hideOperatorDropdown}
			isMulti={isMulti}
			fieldDescription={fieldDescription}
			onOpenOperatorPicker={onOpenOperatorPicker}
			isIrremovable={isIrremovable}
		/>
	);
};
